import React, { useState, useEffect, useRef, useCallback } from "react";

import { useNavigate } from "react-router-dom";
import {
	Search,
	MessageSquare,
	User,
	StickyNote,
	Tag as TagIcon,
	UserPlus,
	Briefcase,
	FileText,
	Loader2,
} from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { cn } from "../../lib/utils";

import api from "../../services/api";

const GRUPOS = {
	contatos: { rotulo: "Contatos", icone: User },
	mensagens: { rotulo: "Mensagens", icone: MessageSquare },
	notas: { rotulo: "Notas internas", icone: StickyNote },
	etiquetas: { rotulo: "Etiquetas", icone: TagIcon },
	leads: { rotulo: "Leads", icone: UserPlus },
	negocios: { rotulo: "Negócios", icone: Briefcase },
	documentos: { rotulo: "Documentos", icone: FileText },
};

// Busca em tudo: contatos, conversas, notas, CRM e documentos. Abre com Ctrl+K.
const GlobalSearch = () => {
	const navigate = useNavigate();
	const [aberto, setAberto] = useState(false);
	const [termo, setTermo] = useState("");
	const [resultado, setResultado] = useState(null);
	const [buscando, setBuscando] = useState(false);
	const campoRef = useRef();

	// Atalho de teclado — quem usa o dia todo não quer tirar a mão do teclado.
	useEffect(() => {
		const aoTeclar = (e) => {
			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				setAberto((v) => !v);
			}
		};

		document.addEventListener("keydown", aoTeclar);
		return () => document.removeEventListener("keydown", aoTeclar);
	}, []);

	useEffect(() => {
		if (aberto) setTimeout(() => campoRef.current?.focus(), 100);
	}, [aberto]);

	const buscar = useCallback(async (texto) => {
		if (texto.trim().length < 2) {
			setResultado(null);
			return;
		}

		setBuscando(true);
		try {
			const { data } = await api.get("/search", { params: { q: texto } });
			setResultado(data);
		} catch (err) {
			// Busca é auxiliar: falhar aqui não deve interromper o trabalho.
			setResultado(null);
		}
		setBuscando(false);
	}, []);

	useEffect(() => {
		const debounce = setTimeout(() => buscar(termo), 350);
		return () => clearTimeout(debounce);
	}, [termo, buscar]);

	const irPara = (link) => {
		setAberto(false);
		setTermo("");
		setResultado(null);
		navigate(link);
	};

	return (
		<>
			<Button
				variant="ghost"
				size="icon"
				aria-label="Pesquisar em tudo"
				title="Pesquisar (Ctrl+K)"
				onClick={() => setAberto(true)}
			>
				<Search className="h-5 w-5" />
			</Button>

			<Dialog open={aberto} onOpenChange={setAberto}>
				<DialogContent className="top-[10%] max-h-[80vh] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-lg">
					<div className="flex items-center gap-2 border-b px-4">
						<Search className="h-4 w-4 shrink-0 text-muted-foreground" />
						<input
							ref={campoRef}
							value={termo}
							onChange={(e) => setTermo(e.target.value)}
							placeholder="Nome, telefone, mensagem, etiqueta…"
							className="h-12 w-full bg-transparent text-sm outline-hidden placeholder:text-muted-foreground"
						/>
						{buscando && (
							<Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
						)}
					</div>

					<div className="max-h-[60vh] overflow-y-auto">
						{termo.trim().length < 2 && (
							<p className="px-4 py-8 text-center text-xs text-muted-foreground">
								Digite ao menos 2 letras para buscar.
							</p>
						)}

						{resultado && resultado.total === 0 && (
							<p className="px-4 py-8 text-center text-sm text-muted-foreground">
								Nada encontrado para “{resultado.termo}”.
							</p>
						)}

						{resultado &&
							Object.entries(resultado.resultados).map(([chave, itens]) => {
								const grupo = GRUPOS[chave] || {
									rotulo: chave,
									icone: Search,
								};
								const Icone = grupo.icone;

								return (
									<section key={chave}>
										<p className="sticky top-0 bg-muted/50 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
											{grupo.rotulo}
										</p>
										<ul>
											{itens.map((item, indice) => (
												<li key={`${chave}-${indice}`}>
													<button
														type="button"
														onClick={() => irPara(item.link)}
														className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted"
													>
														<Icone className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
														<span className="min-w-0 flex-1">
															<span className="block truncate text-sm font-medium">
																{item.titulo}
															</span>
															{item.descricao && (
																<span className="block truncate text-xs text-muted-foreground">
																	{item.descricao}
																</span>
															)}
															{item.contexto && (
																<span className="block text-[11px] text-muted-foreground">
																	{item.contexto}
																</span>
															)}
														</span>
													</button>
												</li>
											))}
										</ul>
									</section>
								);
							})}
					</div>

					<div className="border-t px-4 py-2">
						<p className="text-[11px] text-muted-foreground">
							Atalho: <kbd className="rounded border px-1">Ctrl</kbd> +{" "}
							<kbd className="rounded border px-1">K</kbd>
						</p>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default GlobalSearch;
